/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from "react";
import { Control } from "react-hook-form";

import { DomainSettings, Image, RichText, Theme } from "types/entities";
import { ResponseEnvelope } from "types/responses";

export type BasicSettings = {
  title: string;
  name: string;
  description?: string | ReactNode;
  placeholder?: string;
  control: Control<PortalSettings, any>;
  testId?: string;
  lockedFeature?: boolean;
  lockedFeatureTooltip?: ReactNode | string;
  isDisabled?: boolean;
};

export type BasicCustomDomainSettings = {
  title: string;
  name: string;
  description?: string | ReactNode;
  placeholder?: string;
  control: Control<CustomDomainSettings, any>;
  testId?: string;
};

export type IdentitySettings = BasicSettings & {
  type: string;
};

export type LocaleSettings = BasicSettings & {
  options: string[];
};

export type PortalSettings = Pick<
  DomainSettings,
  | "site_name"
  | "site_description"
  | "date_format"
  | "date_formats"
  | "time_format"
  | "time_formats"
  | "currency"
  | "user_format"
  | "user_formats"
> & {
  theme?: Theme;
  logo?: Image | File;
  policies: {
    can_enable_communications: boolean;
    can_disable_communications: boolean;
    can_enabled_whitelabel: boolean;
    can_disable_whitelabel: boolean;
  };
  favicon?: string | File;
  domain_name: string;
  custom_domain: string;
  currencies: string[];
  announcement_internal?: RichText | string | null;
  announcement_external?: RichText | string | null;
  communications_enabled: boolean;
  branding_enabled: boolean;
  legacy_ui_enabled: boolean;
  ai_features_enabled: boolean;
  default_course_avatar?: Image | null;
};

export type CustomDomainSettings = {
  custom_domain_name: string;
  custom_email_server: CustomEmailSettings;
  custom_ssl: CustomSSLSettings;
  url?: string;
};

export type CustomEmailSettings = {
  email_domain: string;
  email_username: string;
  enabled: boolean;
};

export type CustomSSLSettings = {
  enabled?: boolean;
  expiration_date?: string;
  pem_certificate?: string;
};

export type CSRSettings = {
  country: string;
  state: string;
  locality: string;
  organization: string;
  organizational_unit: string;
  email: string;
  bits: string;
  wildcard_certificate: boolean;
};

export type CustomEmailValidateSettings = {
  is_dkim_valid: boolean;
  dkim_record_host: string;
  dkim_record_key: string;
  is_spf_valid: boolean;
  spf_record_host: string;
};

export type CSRConfiguration = {
  csr: string;
  private_key: string;
};

export type CustomEmailValidateSettingsRes = ResponseEnvelope<CustomEmailValidateSettings>;

export type PortalSettingsRes = ResponseEnvelope<PortalSettings>;

export type DomainNameSettings = { name: string };

export type CustomDomainSettingsRes = ResponseEnvelope<CustomDomainSettings>;

export type DomainNameSettingsRes = ResponseEnvelope<{ url: string }>;

export type CSRConfigurationRes = ResponseEnvelope<CSRConfiguration>;

export type InputFile = Image | string | null;

export enum ImageType {
  LOGO = "logo",
  FAVICON = "favicon",
  DEFAULT_COURSE_AVATAR = "default_course_avatar",
}
